<template>
  <ViewHeader>
    <template #tools>
      <Button
        type="button"
        :text="$t('playRandomStation')"
        hide-text
        rounded
        :loading="stationsApi.loading.value"
        @click="playRandomStation"
      >
        <template #icon>
          <i-mdi-shuffle-variant />
        </template>
      </Button>
    </template>
    <template #footer>
      <Form
        class="search-form"
        role="search"
        :aria-label="$t('searchForm.title')"
        @submit.prevent="onSubmit"
      >
        <InputText
          class="test"
          v-model="formData.q"
          :label="$t('searchForm.query.label')"
          hide-label
          clearable
          :placeholder="$t('searchForm.query.placeholder')"
          :debounce="300"
        >
          <template #icon>
            <i-mdi-search />
          </template>
        </InputText>
        <FormContent v-if="resolvedTags.length">
          <TagList
            v-model="resolvedTags"
            removable
            @item-remove="(_item: Tag) => removeTag()"
          />
        </FormContent>
        <FormContent class="sr-only">
          <Button
            type="submit"
            :text="$t('searchForm.submit.text')"
            tabindex="-1"
          />
        </FormContent>
      </Form>
    </template>
  </ViewHeader>
  <Block>
    <SearchResults
      ref="searchResults"
      :q="formData.q"
      :tag="formData.tag"
      :category="formData.category"
    />
  </Block>
</template>

<script setup lang="ts">
import type { Tag, TagResponse, Station, StationResponse } from '@/types'
import { ref, useTemplateRef, onActivated, onDeactivated } from 'vue'
import { watchPausable } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { useApi } from '@/lib/api'
import { setStation } from '@/lib/player'
import SearchResults from '@/components/SearchResults.vue'

interface SearchTerms {
  q: string
  tag: string
  category: string
}

const searchResultsComponent =
  useTemplateRef<InstanceType<typeof SearchResults>>('searchResults')
const stationsApi = useApi()
const tagsApi = useApi()
const resolvedTags = ref<Tag[]>([])
const formData = ref<SearchTerms>({ q: '', tag: '', category: '' })

const onSubmit = () => {
  if (!formData.value.q) {
    return
  }

  searchResultsComponent.value?.focus()
}

const queryParams = ref({
  tag: useRouteQuery('tag'),
})

const checkTag = async (tag: string | string[] | null = null) => {
  if (!tag) {
    return
  }

  tag = String(tag)
  if (!tag) {
    return
  }

  // Resolve requested tag
  try {
    const response = await tagsApi.ky.get(`tags/${tag}/`).json<TagResponse>()
    resolvedTags.value = [response.data]
  } catch (_e) {
    resolvedTags.value = []
  }

  if (!resolvedTags.value.length) {
    return
  }

  // Tag was found. Accept it as a search term.
  formData.value = {
    ...formData.value,
    q: '',
    tag: tag,
  }
}

const { pause: pauseTagWatch, resume: resumeTagWatch } = watchPausable(
  () => queryParams.value.tag,
  (tag) => {
    checkTag(tag)
  },
)

const removeTag = () => {
  queryParams.value.tag = null
  formData.value.tag = ''
  resolvedTags.value = []
}

const playRandomStation = async (): Promise<Station | undefined> => {
  try {
    const response = await stationsApi.ky
      .get('stations/random')
      .json<StationResponse>()

    if (!response?.data) {
      return undefined
    }

    return setStation(response.data)
  } catch (error) {
    console.error(error)
    return undefined
  }
}

onActivated(() => {
  checkTag(queryParams.value.tag)

  resumeTagWatch()
})

onDeactivated(() => {
  pauseTagWatch()
})
</script>

<style lang="scss" scoped>
.search-form {
  width: 100%;
  max-width: 500px;
}
</style>
